"use client";

import { useCallback } from "react";
import { Capacitor } from "@capacitor/core";

// Lazy load Haptics only on native
let HapticsModule: typeof import("@capacitor/haptics") | null = null;

async function getHaptics() {
  if (!Capacitor.isNativePlatform()) return null;
  if (!HapticsModule) {
    HapticsModule = await import("@capacitor/haptics");
  }
  return HapticsModule.Haptics;
}

export type HapticStyle = "light" | "medium" | "heavy" | "success" | "warning" | "error" | "selection";

/**
 * Hook for native haptic feedback
 * Falls back gracefully on web
 */
export function useHaptics() {
  const impact = useCallback(async (style: HapticStyle = "medium") => {
    try {
      const Haptics = await getHaptics();
      if (!Haptics) return;

      const { ImpactStyle, NotificationType } = await import("@capacitor/haptics");

      switch (style) {
        case "light":
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
        case "medium":
          await Haptics.impact({ style: ImpactStyle.Medium });
          break;
        case "heavy":
          await Haptics.impact({ style: ImpactStyle.Heavy });
          break;
        case "success":
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case "warning":
          await Haptics.notification({ type: NotificationType.Warning });
          break;
        case "error":
          await Haptics.notification({ type: NotificationType.Error });
          break;
        case "selection":
          await Haptics.selectionStart();
          await Haptics.selectionEnd();
          break;
      }
    } catch (e) {
      // Silently fail - haptics are nice-to-have
      console.debug("Haptics unavailable:", e);
    }
  }, []);

  const selectionChanged = useCallback(async () => {
    try {
      const Haptics = await getHaptics();
      if (!Haptics) return;
      await Haptics.selectionChanged();
    } catch {
      // Silently fail
    }
  }, []);

  const vibrate = useCallback(async (duration = 300) => {
    try {
      const Haptics = await getHaptics();
      if (!Haptics) return;
      await Haptics.vibrate({ duration });
    } catch {
      // Silently fail
    }
  }, []);

  return {
    impact,
    selectionChanged,
    vibrate,
  };
}

/**
 * Simple haptic trigger for one-off use without hook
 */
export async function triggerHaptic(style: HapticStyle = "medium") {
  try {
    const Haptics = await getHaptics();
    if (!Haptics) return;

    const { ImpactStyle, NotificationType } = await import("@capacitor/haptics");

    switch (style) {
      case "light":
        await Haptics.impact({ style: ImpactStyle.Light });
        break;
      case "medium":
        await Haptics.impact({ style: ImpactStyle.Medium });
        break;
      case "heavy":
        await Haptics.impact({ style: ImpactStyle.Heavy });
        break;
      case "success":
        await Haptics.notification({ type: NotificationType.Success });
        break;
      case "warning":
        await Haptics.notification({ type: NotificationType.Warning });
        break;
      case "error":
        await Haptics.notification({ type: NotificationType.Error });
        break;
      case "selection":
        await Haptics.selectionStart();
        await Haptics.selectionEnd();
        break;
    }
  } catch {
    // Silently fail
  }
}
